import { comparer, toJS } from 'mobx';

import { AnalyticsStore, codeSplit, ContextStore, Controller, ExperimentStore, FetchOptions, merge, PageStore } from 'bernie-core';
import { PageData } from 'bernie-http';

import { CustomerInterestsStore } from 'stores/customer-interests-store';
import { PageName } from 'constants/pages';
import { getSearchImpactingKeys, SearchCriteriaModel, singleUseQueryParams, untrackedQueryParams } from 'stores/models';
import { SearchCriteriaStore } from 'stores/search-criteria-store';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { SearchResultsStore } from 'stores/search-results-store';

import { meta } from 'utils/meta';
import { convertHashParams, shouldHashOverrideDefaultValues, shouldSearchHashParams } from './converters/legacy-hash-params';
import { CustomerInterest } from 'models/common/customer-interest';
import { MultiItemStore } from 'stores/multi-item-store';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

const { withoutKeys, withKeys } = merge;

export class SearchController implements Controller {
  public bundles = ['search'];
  public component = codeSplit(() => import(/* webpackChunkName: "search" */ 'views/hotel/search.jsx'));
  public pageId = `${PageName.SEARCH},H,20`;
  public path = '/Hotel-Search*';
  public exact = true;
  public routeName = 'search';
  public routeData = {
    rfrrNamespace: 'HOT.SR',
    pageName: 'page.Hotel-Search'
  };

  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('SearchController.fetch receieved an invalid StoreContainer');
    }

    const { stores, query, isServer } = options;

    const context: ContextStore = stores.get<ContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const searchCriteria: SearchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteria');
    const searchResults: SearchResultsStore = stores.get<SearchResultsStore>('searchResults');
    const experiments: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const customerInterestsStore: CustomerInterestsStore = stores.get<CustomerInterestsStore>('customerInterestsStore');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');

    searchResults.pageData = page;

    // BexApi Property Search Experiment
    const bexApi = experiments.exposures.PWA_HSR_BEXAPI_Call_Throttle;
    if (bexApi) {
      experiments.logExperiment(context, page, bexApi);
      searchResults.bexApi = bexApi.bucket;
    }

    // Building Multi-Item shopping context
    multiItem.buildContext(query);

    // sanitize incoming params
    const singleUseCriteria: any = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...singleUseCriteria, 'prevQuery'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery = withoutKeys(untrackedKeys).merge({}, query);

    if (searchResults.foregoClientFetch) {
      searchResults.foregoClientFetch = false;

      const criteria = searchResults.searchCriteria.toJSON();
      const sqaParsedOut = SearchQueryAdapter.parseOut(criteria);

      return { query: sqaParsedOut };
    }

    // if client side detects hash params, handle them
    if (!isServer && shouldSearchHashParams(nextQuery)) {
      const hashParams = convertHashParams();

      if (shouldHashOverrideDefaultValues(hashParams)) {
        searchResults.defaultRequestFields = {};
      }

      if (hashParams) {
        return { query: hashParams };
      }
    }

    // server rendered with defaulted params, we need to adjust URLs first
    if (this.shouldProcessClientSideDefaultRequestFields(isServer, searchResults, nextQuery)) {
      return this.updateQuery(searchResults);
    }

    const forceFetch = searchResults.shouldFetch;

    // check if queries are different to previous state
    if (forceFetch || !comparer.structural(nextQuery, prevQuery)) {
      // queries are meaningfully diff, setup base stores again
      searchCriteria.setValues({
        ...SearchCriteriaStore.getDefaults(),
        ...SearchCriteriaStore.getFilterDefaults(),
        ...nextQuery
      });

      const modelData = SearchQueryAdapter.parseIn(searchCriteria.toJSON());
      const incomingSearchCriteria = new SearchCriteriaModel(modelData);
      let customerInterests: CustomerInterest[] = customerInterestsStore.customerInterests;
      const multiItemContext = multiItem ? multiItem.multiItemContext : null;

      if (this.shouldFetchCustomerInterests(experiments, isServer, bexApi)) {
        experiments.logExperiment(context, page, experiments.exposures.PWA_Affinity_from_customer_spinner);

        customerInterests = await customerInterestsStore.fetch(context, incomingSearchCriteria.semdtl);

        if (customerInterests.length > 0) {
          searchResults.foregoClientFetch = true;
        }
      }

      // fetches are only carried out if criteria is also changed
      if (forceFetch || this.hasStoresChanged(searchResults.searchCriteria, incomingSearchCriteria)) {
        const skipSearchResultsFetch = isServer === true && query && query.skipServerFetch === 'true';

        await this.doNetworking(
          page,
          analyticsStore,
          context,
          searchResults,
          incomingSearchCriteria,
          skipSearchResultsFetch,
          singleUseCriteria,
          searchCriteria,
          experiments,
          customerInterests,
          multiItemContext
        );
      }

      if (!isServer && searchResults.alternateDateFetchUrl) {
        searchResults.fetchAlternateDates(context, searchResults.alternateDateFetchUrl);
      }
    }

    // cleanup defaultRequestFields so they don't hang around for subsequent calls on clientside
    if (this.shouldProcessClientSideDefaultRequestFields(isServer, searchResults, nextQuery)) {
      return this.updateQuery(searchResults);
    }
  }

  private updateQuery(searchResults: SearchResultsStore) {
    const { defaultRequestFields } = searchResults;
    searchResults.defaultRequestFields = {};

    return { query: toJS(defaultRequestFields) };
  }

  private shouldFetchCustomerInterests(experiments: ExperimentStore, isServer, bexApi): boolean {
    const { PWA_Affinity_from_customer_spinner } = experiments.exposures;

    return (
      isServer && bexApi && bexApi.bucket === 2 && PWA_Affinity_from_customer_spinner && PWA_Affinity_from_customer_spinner.bucket === 1
    );
  }

  private async doNetworking(
    page: PageStore,
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    searchResults: SearchResultsStore,
    incomingSearchCriteria: SearchCriteriaModel,
    skipServerFetch: boolean,
    singleUseCriteria: any,
    searchCriteria: SearchCriteriaStore,
    experiments: ExperimentStore,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext
  ) {
    const pageFetch = page.fetch(meta['hotel-search'], context.site, context.locale);

    searchResults.updateSearchCriteria(incomingSearchCriteria);
    const searchResultsFetch = skipServerFetch
      ? Promise.resolve()
      : searchResults.fetch(analyticsStore, context, customerInterests, experiments, singleUseCriteria, multiItemContext);

    await pageFetch;
    await searchResultsFetch;

    this.updateSearchCriteriaFromSearchResults(searchResults, searchCriteria);
  }

  public fetchPageData(): Promise<PageData> {
    // Temporary fix for issue: https://jira.expedia.biz/browse/BHS-17025
    const noIndexSeo = {
      seo: {
        robots: 'noindex,nofollow'
      }
    };

    return Promise.resolve(noIndexSeo) as Promise<PageData>;
  }

  private shouldProcessClientSideDefaultRequestFields(isServer: boolean, searchResults: SearchResultsStore, nextQuery?: object): boolean {
    const { defaultRequestFields } = searchResults;

    if (!isServer && defaultRequestFields && typeof defaultRequestFields === 'object' && Object.keys(defaultRequestFields).length > 0) {
      return (
        !nextQuery ||
        Object.keys(defaultRequestFields).some(key => Boolean(!nextQuery[key] || nextQuery[key] !== defaultRequestFields[key]))
      );
    }

    return false;
  }

  private updateSearchCriteriaFromSearchResults(searchResults, searchCriteria) {
    // BFF may provide regionId when coming from SEM or alternate dates
    if (searchResults.searchCriteria) {
      searchCriteria.startDate = searchResults.searchCriteria.startDate;
      searchCriteria.endDate = searchResults.searchCriteria.endDate;
      searchCriteria.regionId = searchResults.searchCriteria.regionId;
    }
  }

  private getComparableKeys(prevStore: SearchCriteriaModel, newStore: any) {
    let compareKeys = getSearchImpactingKeys();
    // Detects meaningful changes in the geo-related params
    if (!this.didGeoChange(prevStore, newStore)) {
      compareKeys = compareKeys.filter(key => key !== 'regionId' && key !== 'latLong' && key !== 'destination');
    }

    /*
     If we're asking to sort by values that's equivalent to what we currently have,
     remove sort field from the comparison to avoid another service call
     */
    const askingForEmptySortWhileWeHaveRecommended = newStore.sort === '' && prevStore.sort === 'recommended';
    const askingForMctc2SortWhileWeHaveDeals = !newStore.sort && newStore.mctc === '2' && prevStore.sort === 'deals';
    const equivalentSort = askingForEmptySortWhileWeHaveRecommended || askingForMctc2SortWhileWeHaveDeals;
    if (equivalentSort) {
      compareKeys = compareKeys.filter(key => key !== 'sort');
    }

    return compareKeys;
  }

  private didGeoChange(prevStore: SearchCriteriaModel, newStore: any) {
    const { regionId: prevRegionId, latLong: prevLatLong, destination: prevDestination } = prevStore;
    const { regionId: newRegionId, latLong: newLatLong, destination: newDestination } = newStore;

    const isNewRegionIdEmpty = newRegionId === '';
    const isNewLatLongEmpty = newLatLong === '';
    const isNewDestinationEmpty = newDestination === '';
    const didRegionIdChange = !isNewRegionIdEmpty && newRegionId !== prevRegionId;
    const didLatLongChange = !isNewLatLongEmpty && newLatLong !== prevLatLong;
    const didDestinationChange = !isNewDestinationEmpty && newDestination !== prevDestination;

    if (didRegionIdChange) {
      return true;
    }
    if (isNewRegionIdEmpty && didLatLongChange) {
      return true;
    }

    return isNewRegionIdEmpty && isNewLatLongEmpty && didDestinationChange;
  }

  private hasStoresChanged(prevStore: SearchCriteriaModel, newStore: SearchCriteriaModel) {
    if (Boolean(newStore) && !Boolean(prevStore)) {
      return true;
    }

    const compareKeys = this.getComparableKeys(prevStore, newStore);
    const prevSearchParams = withKeys(compareKeys).merge({}, prevStore.toJSON());
    const newSearchParams = withKeys(compareKeys).merge({}, newStore.toJSON());

    return !comparer.structural(prevSearchParams, newSearchParams);
  }
}
